'use client';

import { Button } from '@workspace/ui/components/button';
import { useRouter } from 'next/navigation';
import { Trailers } from './components/Trailers';
import { Screenshots } from './components/Screenshots';
import { Icon } from '@/src/components/ui/Icon';
import { ESystem, IProject } from 'types/src/types';
import { TagsProjectInput } from './components/TagsProjectInput';
import { Report } from '../bits/components/Report';
import {
  ProjectDescription,
  ProjectDetails,
  ProjectStats,
  ProjectTitle,
} from './components/ProjectComponents';
import { TooltipComponent } from '@/src/components/layout/TooltipComponent';
import { useProfile } from '@/src/hooks/use-profile';
import { useBoolean } from '@/src/hooks/use-boolean';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu';
import { ShareLink } from '../bits/components/ShareLink';
import PageLoader from '@/src/components/ui/PageLoader';
import { deleteProject, setFollow, setWishlist } from '@/src/actions/projects';
import { cn } from '@workspace/ui/lib/utils';
import { DownloadsDropdown } from './components/DownloadsDropdown';
import { useEffect, useMemo, useState } from 'react';
import { Spinner } from '@/src/components/ui';
import { PublishStatusBadge } from './components/PublishStatusBadge';
import { createPurchaseCheckoutSession } from '@/src/actions/stripe';
import Image from 'next/image';
import { ReviewsSection } from '../reviews/components/ReviewsSection';
import { IReviewData } from '@/src/types/interfaces';
import { isNoFileUploaded } from '@/src/utils/helpers';
import { HorizontalBitsCarousel } from '@/src/components/ui/HorizontalBitsList';
import { PayPalPurchaseButtons } from './components/PayPalPurchaseButtons';
import { NoticeDialog } from '@/src/components/ui/NoticeDialog';

type ProjectState =
  | 'has-download-access'
  | 'payment-pending'
  | 'downloaded'
  | 'free'
  | 'not-paid'
  | 'other'
  | 'no-file';

type UiPurchaseStatus = 'idle' | 'pending' | 'completed' | 'failed';
type UiPurchaseResult = 'idle' | 'processing' | 'success' | 'error';

type Props = {
  project: IProject;
  reviewsData: IReviewData;
};

export default function Project({ project, reviewsData }: Props) {
  const router = useRouter();
  const type =
    project.contentType[0]?.toUpperCase() + project.contentType.slice(1);
  const { data: currentProfile } = useProfile();
  const isOpenDialog = useBoolean(false);
  const isDeleting = useBoolean(false);
  const isMyProject = project.author.id === currentProfile?.id;
  const [purchaseStatus, setPurchaseStatus] =
    useState<UiPurchaseStatus>('idle');

  const handleDelete = async () => {
    try {
      isDeleting.onTrue();
      await deleteProject(project.id);
      router.push(`/${project.author.uniqueLink}`);
    } catch (error) {
      console.error('Error deleting project:', error);
    } finally {
      isDeleting.onFalse();
    }
  };

  const [purchaseResult, setPurchaseResult] =
    useState<UiPurchaseResult>('idle');

  const { data: profile, isLoading } = useProfile();
  const profileId = profile?.id;
  const isLoadingPage = useBoolean(false);
  const isAnonymous = profile?.type === 'anonymous';
  const isWished = useBoolean(project.isWished);
  const isFollowed = useBoolean(project.isFollowed);
  const isDownloaded = useBoolean(false);
  const isDownloading = useBoolean(false);
  const withoutFile = isNoFileUploaded(project.fileSize as string);

  const isPurchaseOpen = useBoolean(false);

  const handleBuyNow = async () => {
    isLoadingPage.onTrue();

    if (isAnonymous) {
      router.push('/sign-in');
      return;
    }

    if (!!project.author.stripeAccountId && !project.author.payPalRecipient) {
      const { url } = await createPurchaseCheckoutSession(
        profileId as number,
        project.id,
        project.author.stripeAccountId as string,
      );
      if (url) window.location.href = url;
      return;
    }

    isLoadingPage.onFalse();
    isPurchaseOpen.onTrue();
  };

  const handleSetWished = async () => {
    isLoadingPage.onTrue();
    if (isAnonymous) {
      router.push('/sign-in');
      return;
    }
    try {
      isWished.onToggle();
      const { data, error } = await setWishlist(project.id);
      if (error) {
        console.error('Error wishing project:', error);
      }
    } catch (error) {
    } finally {
      isLoadingPage.onFalse();
    }
  };

  const handleSetFollowed = async () => {
    isLoadingPage.onTrue();
    if (isAnonymous) {
      router.push('/sign-in');
      return;
    }
    try {
      isFollowed.onToggle();
      const { data, error } = await setFollow(project.id);
      if (error) {
        console.error('Error following project:', error);
      }
    } catch (error) {
    } finally {
      isLoadingPage.onFalse();
    }
  };

  const handleGoToEdit = () => {
    isLoadingPage.onTrue();
    router.push(`/project/${project.slug}/edit`);
  };

  useEffect(() => {
    if (purchaseResult !== 'processing') return;

    let stopped = false;
    let attempts = 0;
    const MAX = 20;

    const tick = async () => {
      if (stopped) return;

      attempts += 1;
      if (!stopped && attempts < MAX) {
        setTimeout(tick, 1500);
      }
    };
    setPurchaseStatus('pending');
    tick();

    return () => {
      stopped = true;
    };
  }, [purchaseResult, project.id, router]);

  useEffect(() => {
    if (!project.hasDownloadAccess) return;

    setPurchaseResult('success');
    setPurchaseStatus('completed');
  }, [project.hasDownloadAccess]);

  const downloaded = project.isDownloadedByCurrentUser || isDownloaded.value;
  const reviewed = project.isRatedByCurrentUser;

  const hasAndroid = useMemo(() => {
    const byFiles = project.files?.some(
      (f) => f.system === ESystem.ANDROID && !!f.fileUrl,
    );
    return byFiles;
  }, [project.files]);

  const hasIos = useMemo(() => {
    const byFiles = project.files?.some(
      (f) => f.system.toUpperCase() === ESystem.IOS && !!f.fileUrl,
    );
    return byFiles;
  }, [project.files]);

  const openStoreLink = (system: ESystem) => {
    const url = project.files?.find(
      (f) => f.system === system && !!f.fileUrl,
    )?.fileUrl;
    if (url) window.open(url, '_blank');
  };

  const price =
    Number(project.price) > 0 ? `$${Number(project.price).toFixed(2)}` : 'Free';

  const getProjectState = (): ProjectState => {
    if (downloaded) return 'downloaded';
    if (project.hasDownloadAccess) return 'has-download-access';

    if (purchaseResult === 'processing' || purchaseStatus === 'pending') {
      return 'payment-pending';
    }

    if (Number(project.price) > 0) return 'not-paid';
    if (withoutFile) return 'no-file';
    if (Number(project.price) === 0) return 'free';

    return 'other';
  };

  const projectState = getProjectState();

  return (
    <div className="w-full max-w-[1200px] md:p-0 p-2">
      {isLoading || (isLoadingPage.value && <PageLoader />)}
      <div className="gap-4 w-full md:grid lg:grid-cols-10 xl:grid-cols-12 md:gap-[53px]">
        <div className="flex flex-col h-max lg:col-span-4 md:pt-5 md:pl-0">
          {project.coverImageUrl ? (
            <Image
              src={project.coverImageUrl}
              alt="Cover"
              className="size-[191px] rounded-xl border"
              width={191}
              height={191}
            />
          ) : null}
          <div className="flex items-start justify-between md:mt-0 mt-3">
            <ProjectTitle title={project.title} className="" />
            {!project.isMyProject && (
              <ProjectDropdownMenu
                slug={project.slug}
                isMyProfile={project.author.id === profileId}
                project={project}
                isAnonymous={isAnonymous}
                downloaded={downloaded}
              />
            )}
          </div>
          <ProjectDescription description={project.shortDescription} />
          <TagsProjectInput tags={project.tags} setTags={() => {}} readonly />
          <ProjectStats
            downloads={project.downloads}
            rating={project.rating.average as number}
            type={project.contentType}
            developer={project.author.fullName}
            isPartner={project.author.isPartner}
            devLink={project.author.uniqueLink}
            preview={false}
          />
          <hr className="border-t border-gray-200 md:mt-5 my-1" />
          {!isMyProject && !downloaded && !withoutFile && (
            <span
              className={cn(
                'font-semibold text-2xl mt-6 flex items-center gap-2',
                {
                  'text-black/40': project.hasDownloadAccess,
                },
              )}
            >
              {price}
              {project.hasDownloadAccess ? (
                <>
                  <hr className="border-r border-gray-200 h-5" />
                  <span className="font-normal text-sm">Purchased</span>
                </>
              ) : null}
            </span>
          )}
          {!project.isMyProject
            ? !isLoading && (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-row md:mt-5 mt-3 gap-2 w-fit md:justify-between items-center">
                    {projectState === 'not-paid' && (
                      <Button
                        size="sm"
                        className="w-[180px]"
                        onClick={handleBuyNow}
                      >
                        Buy Now
                      </Button>
                    )}
                    {projectState === 'downloaded' && (
                      <Button
                        size="sm"
                        className="w-[180px]"
                        disabled={downloaded}
                      >
                        Downloaded
                      </Button>
                    )}
                    {projectState === 'payment-pending' && (
                      <Button size="sm" disabled className="w-[180px]">
                        <Spinner /> Processing payment…
                      </Button>
                    )}

                    {(projectState === 'free' ||
                      projectState === 'has-download-access') && (
                      <DownloadsDropdown
                        projectFiles={project.files}
                        isAnonymous={isAnonymous}
                        isSubmenu={false}
                        isDownloaded={isDownloaded}
                        isDownloading={isDownloading}
                        isMyProject={false}
                      >
                        <Button
                          size="sm"
                          className="md:w-[180px] w-36"
                          disabled={downloaded}
                        >
                          {isDownloading.value ? (
                            <>
                              Downloading <Spinner />
                            </>
                          ) : (
                            'Download'
                          )}
                        </Button>
                      </DownloadsDropdown>
                    )}
                    <Button
                      size="sm"
                      variant={isFollowed.value ? 'gray' : 'secondary'}
                      className="md:w-[180px] w-36"
                      onClick={handleSetFollowed}
                    >
                      {isFollowed.value ? 'Unfollow' : 'Follow'}
                    </Button>
                    <TooltipComponent
                      text={
                        isWished.value
                          ? 'Remove from Wishlist'
                          : 'Add to Wishlist'
                      }
                      className="w-fit"
                    >
                      <Button
                        type="button"
                        size="icon"
                        variant="gray"
                        className={cn('text-black h-8', {
                          'bg-black/80 text-white hover:bg-black hover:text-white':
                            isWished.value,
                        })}
                        onClick={handleSetWished}
                      >
                        <Icon name={isWished.value ? 'liked' : 'like'} />
                      </Button>
                    </TooltipComponent>
                  </div>
                  <div className="flex gap-5">
                    {hasAndroid && (
                      <Icon
                        name="getOnGoogle"
                        className="w-28 h-9 cursor-pointer"
                        onClick={() => openStoreLink(ESystem.ANDROID)}
                      />
                    )}
                    {hasIos && (
                      <Icon
                        name="getOnAppStore"
                        className="w-28 h-9 cursor-pointer"
                        onClick={() => openStoreLink(ESystem.IOS)}
                      />
                    )}
                  </div>
                </div>
              )
            : !isLoading && (
                <div className="flex justify-between items-center md:mt-5 mt-3">
                  <span className="font-semibold md:text-2xl! text-lg!">
                    {price}
                  </span>
                  <Button
                    variant="gray"
                    size="sm"
                    className="w-56"
                    onClick={handleGoToEdit}
                  >
                    <a href={`/project/${project.slug}/edit`}>
                      Edit {type.toLowerCase()}
                    </a>
                  </Button>
                  <ProjectDropdownMenu
                    slug={project.slug}
                    isMyProfile={project.author.id === profileId}
                    project={project}
                    onRequestDelete={isOpenDialog.onTrue}
                    isAnonymous={isAnonymous}
                    downloaded={downloaded}
                  />
                </div>
              )}
          <ProjectDescription description={project.description} isReachText />
          {
            <>
              <hr className="border-t border-black/5 md:my-5" />
              <ReviewsSection
                isProjectPage={true}
                projectSlug={project.slug}
                reviewed={reviewed}
                downloaded={downloaded}
                project={project}
                reviewsData={reviewsData}
              />
            </>
          }
        </div>
        <div className="col-span-2 md:pt-4 pt-2 lg:col-span-6 xl:col-span-8">
          <div className="flex flex-col justify-between md:gap-5">
            <PublishStatusBadge
              status={project.publishingStatus}
              purchased={project.hasDownloadAccess ?? false}
              isMyProject={isMyProject}
            >
              <h3 className="md:text-2xl! text-lg! font-semibold!">
                {type} Details
              </h3>
            </PublishStatusBadge>

            <ProjectDetails
              size={project.fileSize as string}
              status={project.status}
              systems={project.systems}
              maturityRating={project.maturityRating}
              version={project.version}
              placement="right"
              withoutFile={withoutFile}
            />
          </div>

          {!!project.trailers?.length && (
            <Trailers trailers={project.trailers} readonly />
          )}

          {!!project.screenshotsUrls?.length && (
            <Screenshots screenshots={project.screenshotsUrls} readonly />
          )}

          {project.relatedBits && (
            <>
              <h3 className="md:text-2xl! text-lg! font-semibold! mt-5">
                Stories
              </h3>
              <div className="text-black/30 mt-2 mb-10">
                <HorizontalBitsCarousel bits={project.relatedBits} />
              </div>
            </>
          )}
        </div>
      </div>
      <Dialog open={isOpenDialog.value} onOpenChange={isOpenDialog.setValue}>
        <DialogContent className="flex flex-col gap-0 md:min-w-[540px] custom-close-dialog rounded-2xl min-w-full min-h-screen md:min-h-fit">
          <DialogHeader className="block">
            <h3 className="md:text-2xl text-sm font-semibold">
              Delete this project?
            </h3>
            <p className="md:text-base text-sm font-normal text-black/50 mt-2">
              This will permanently remove the project and its files. This
              action cannot be undone.
            </p>
            <DialogTitle />
            <DialogDescription />
          </DialogHeader>

          <hr className="my-4 border-t border-bg-gray" />

          <DialogFooter>
            <div className="flex justify-between w-full">
              <DialogClose asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="gray"
                  onClick={isOpenDialog.onFalse}
                  className="px-4 py-2"
                >
                  Cancel
                </Button>
              </DialogClose>

              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={handleDelete}
                className="px-4 py-2"
              >
                {isDeleting.value && <Spinner />} Delete project
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isPurchaseOpen.value && (
        <div className="fixed inset-0 z-50 flex justify-center items-start md:items-center bg-black/30 p-2 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white p-5 rounded-2xl shadow-md flex flex-col relative w-[95%] md:w-[540px]">
            <div className="flex flex-col gap-2">
              <h3 className="text-3xl! font-semibold! text-center">
                Confirm purchase
              </h3>
              <p className="text-black/60 font-light">
                You’re about to buy{' '}
                <span className="font-medium text-black">{project.title}</span>{' '}
                by{' '}
                <span className="font-medium text-black">
                  {project.author.fullName}
                </span>{' '}
                for{' '}
                <span className="font-semibold text-black">
                  ${Number(project.price).toFixed(2)}
                </span>
                .
              </p>
            </div>

            <hr className="w-full border-gray-100 my-5" />

            <PayPalPurchaseButtons
              projectId={project.id}
              onSuccess={() => {
                setPurchaseResult('processing');
                setPurchaseStatus('pending');
                isPurchaseOpen.onFalse();
              }}
              onCancel={() => {
                isPurchaseOpen.onFalse();
                setPurchaseResult('idle');
                setPurchaseStatus('idle');
              }}
              onError={(e) => {
                console.error(e);
                isPurchaseOpen.onFalse();
                setPurchaseResult('error');
                setPurchaseStatus('failed');
              }}
            />

            <hr className="w-full border-gray-100 my-5" />

            <div className="flex items-center justify-end">
              <Button
                variant="outline"
                size="sm"
                className="bg-transparent px-4 h-7"
                onClick={isPurchaseOpen.onFalse}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* <NoticeDialog
        openOnLoad={purchaseResult === 'processing'}
        title="Processing payment…"
        description="This may take a few seconds."
        actionLabel="OK"
      /> */}
    </div>
  );
}
//________________________________________________________________

export const ProjectDropdownMenu = ({
  slug,
  isMyProfile,
  project,
  className,
  onRequestDelete,
  isAnonymous,
  downloaded,
}: {
  slug: string;
  isMyProfile: boolean;
  isAnonymous: boolean;
  project: IProject;
  className?: string;
  handleDownload?: () => void;
  onRequestDelete?: () => void;
  downloaded: boolean;
}) => {
  const menuOpen = useBoolean(false);
  const reportOpen = useBoolean(false);
  return (
    <>
      <DropdownMenu modal={false} onOpenChange={menuOpen.setValue}>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="gray"
            className={`text-black size-8 bg-white ${className}`}
          >
            <Icon name="dots" className="rotate-90" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="font-normal p-2 text-black/40 text-base"
        >
          {!isMyProfile && (
            <>
              {downloaded && (
                <DownloadsDropdown
                  projectFiles={project.files}
                  isAnonymous={isAnonymous}
                  isMyProject={false}
                  isSubmenu={true}
                  submenuLabel={
                    <>
                      <Icon name="redownload" className="size-3.5" />
                      <span>Re-download</span>
                    </>
                  }
                />
              )}
              {/* <DropdownMenuItem className="flex items-center py-0 px-1 cursor-pointer">
                <Button
                  disabled
                  variant="outline"
                  size="sm"
                  className="w-full h-fit border-0 gap-2 md:py-1 bg-transparent flex items-center justify-start shadow-none font-medium text-sm px-1"
                  // onClick={refundHandler}
                >
                  <Icon name="refund" className="md:mr-1 size-3.5" />
                  Refund
                </Button>
              </DropdownMenuItem> */}
            </>
          )}
          <DropdownMenuItem className="flex items-center py-0 px-1 cursor-pointer">
            <ShareLink
              type="project"
              slug={slug}
              className="w-full h-fit! border-0 py-1 bg-transparent flex items-center justify-start shadow-none font-medium text-sm px-1! text-black/40"
            />
          </DropdownMenuItem>

          {isMyProfile ? (
            <>
              <DownloadsDropdown
                projectFiles={project.files}
                isAnonymous={isAnonymous}
                isMyProject={true}
                isSubmenu={true}
                submenuLabel={
                  <>
                    <Icon name="redownload" className="size-3.5" />
                    <span>Download</span>
                  </>
                }
              />
              <DropdownMenuItem className="flex items-center py-0 px-1 cursor-pointer">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-fit h-fit border-0 gap-2 md:py-1 text-red-error bg-transparent flex items-center font-medium text-sm justify-start shadow-none px-1"
                  onClick={() => {
                    menuOpen.onFalse();
                    if (onRequestDelete) {
                      onRequestDelete();
                    }
                  }}
                >
                  <Icon
                    name="delete"
                    className="md:mr-1 size-3.5 text-red-error"
                  />
                  Delete
                </Button>
              </DropdownMenuItem>
            </>
          ) : (
            <>
              <DropdownMenuItem className="flex items-center py-0 px-1 cursor-pointer">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-fit border-0 gap-2 md:py-1 bg-transparent flex items-center justify-start shadow-none font-medium text-sm px-1 text-red-error"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    menuOpen.onFalse();
                    setTimeout(() => reportOpen.onTrue(), 0);
                  }}
                >
                  <Icon name="report" className="mr-1 size-4 text-red-error" />
                  Report
                </Button>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <Report
        type="project"
        projectId={project.id}
        hideTrigger
        open={reportOpen.value}
        onOpenChange={(o) => {
          if (!o) {
            menuOpen.onFalse();
          }
          reportOpen.setValue(o);
        }}
      />
    </>
  );
};
